import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { APP_ROUTES, AUTH_PATH } from '@core/constants';
import { AuthService } from '@core/services';
import { UserAiStore } from '@core/stores';
import { NgIconsModule, provideIcons } from '@ng-icons/core';
import {
  heroEllipsisHorizontalMini,
  heroXMarkMini,
} from '@ng-icons/heroicons/mini';
import {
  heroArrowRightStartOnRectangle,
  heroEye,
  heroEyeSlash,
  heroKey,
  heroLockClosed,
} from '@ng-icons/heroicons/outline';
import {
  heroQuestionMarkCircleSolid,
  heroUserSolid,
} from '@ng-icons/heroicons/solid';
import { ClickOutsideDirective } from '@shared/directives';
import { CustomValidators } from '@shared/validators';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import {
  DxButton,
  DxDialog,
  DxError,
  DxFormField,
  DxInput,
  DxLabel,
  DxPrefix,
  DxSnackBar,
  DxSuffix,
} from '@v-ui';
import moment from 'moment/moment';

@Component({
  selector: 'app-user-info-config',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIconsModule,
    MatIconModule,
    MatMenuModule,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    ClickOutsideDirective,
    DxButton,
    DxError,
    DxFormField,
    DxInput,
    DxLabel,
    DxPrefix,
    DxSuffix,
    ThemeToggleComponent,
  ],
  templateUrl: './user-info-config.component.html',
  styleUrls: ['./user-info-config.component.css'],
  providers: [
    provideIcons({
      heroQuestionMarkCircleSolid,
      heroEllipsisHorizontalMini,
      heroUserSolid,
      heroXMarkMini,
      heroLockClosed,
      heroEyeSlash,
      heroEye,
      heroKey,
      heroArrowRightStartOnRectangle,
    }),
  ],
})
export class UserInfoConfigComponent implements OnInit {
  showMenuUserInfo = signal<boolean>(false);
  showConfirmPassword = signal<boolean>(false);
  showOldPassword = signal<boolean>(false);
  showPassword = signal<boolean>(false);

  formGroupChangePass!: FormGroup;
  changePassDialogRef: any;

  userAiStore = inject(UserAiStore);
  private router = inject(Router);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private dialog = inject(DxDialog);
  private snackBar = inject(DxSnackBar);

  /** Tên hiển thị trên popup: họ tên → username → email. */
  get currentUserName(): string {
    const u = this.userAiStore.currentUser();
    if (!u) return '';
    const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
    return name || u.username || u.email || '';
  }

  /** 1-2 ký tự viết tắt cho avatar gradient. */
  get currentUserInitials(): string {
    const u = this.userAiStore.currentUser();
    if (!u) return '';
    const initials = `${u.first_name?.[0] ?? ''}${
      u.last_name?.[0] ?? ''
    }`.trim();
    return (
      initials || (u.username?.[0] ?? u.email?.[0] ?? '?')
    ).toUpperCase();
  }

  get formControlPassword(): FormArray {
    return this.formGroupChangePass.get('password') as FormArray;
  }

  get formControlConfirmPassword(): FormArray {
    return this.formGroupChangePass.get('confirmPassword') as FormArray;
  }

  ngOnInit(): void {
    this.formGroupChangePass = this.fb.group(
      {
        password: [
          null,
          [
            Validators.required,
            Validators.minLength(8),
            CustomValidators.patternValidator(/\d/, { hasNumber: true }),
            CustomValidators.patternValidator(
              /[A-ZÀÁẠẢÃÂẦẤẬẨẪĂẰẮẶẲẴÈÉẸẺẼÊỀẾỆỂỄÌÍỊỈĨÒÓỌỎÕÔỒỐỘỔỖƠỜỚỢỞỠÙÚỤỦŨƯỪỨỰỬỮỲÝỴỶỸĐ]/,
              { hasCapitalCase: true }
            ),
            CustomValidators.patternValidator(
              /[a-zàáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/,
              { hasSmallCase: true }
            ),
            CustomValidators.patternValidator(
              /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/,
              {
                hasSpecialCharacters: true,
              }
            ),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
        oldPassword: ['', [Validators.required]],
      },
      {
        validators: [
          CustomValidators.mustMatch('password', 'confirmPassword'),
          CustomValidators.mustNotMatch('oldPassword', 'password'),
        ],
      }
    );
  }

  changePassword(template: any) {
    this.changePassDialogRef = this.dialog.open(template, {
      data: {},
      width: '30vw',
      minWidth: '340px',
    });
  }

  closeDialogChangePass() {
    this.showOldPassword.set(false);
    this.showPassword.set(false);
    this.showConfirmPassword.set(false);
    this.changePassDialogRef.close();
    this.formGroupChangePass.reset();
  }

  toggleVisibility(key: string) {
    if (key === 'password') {
      this.showPassword.set(!this.showPassword());
    } else if (key === 'confirmPassword') {
      this.showConfirmPassword.set(!this.showConfirmPassword());
    } else if (key === 'oldPassword') {
      this.showOldPassword.set(!this.showOldPassword());
    }
  }

  onSavePasswordChange(data: any) {
    const body = {
      old_password: data.oldPassword.trim(),
      new_password: data.password.trim(),
    };
    this.authService.changePassword(body).subscribe({
      next: (res) => {
        this.closeDialogChangePass();
        this.snackBar.open(res.detail, '', {
          panelClass: 'dx-snack-bar-success',
          duration: 5000,
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      },
    });
  }

  signOut() {
    this.authService.logout();
    this.router.navigate([APP_ROUTES.AUTH + '/' + AUTH_PATH.LOGIN]);
  }

  openMenuUserInfo() {
    this.showMenuUserInfo.set(!this.showMenuUserInfo());
  }

  handleUserPlan(): string | undefined {
    {
      return '';
    }
  }

}
